import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  LinearProgress,
  IconButton,
  TextField,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { i18n } from "../../translate/i18n";
import { getGPTTranslation, getGPTLanguage, getInitialLanguageFromI18n } from "../../utils/gptTranslations";

const useStyles = makeStyles((theme) => ({
  dialog: {
    [theme.breakpoints.up('sm')]: {
      minWidth: 500,
    },
  },
  dialogContent: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1),
    },
  },
  suggestionItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    cursor: "pointer",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.main,
    },
  },
  suggestionText: {
    marginBottom: theme.spacing(1),
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  confidenceChip: {
    marginTop: theme.spacing(1),
  },
  quickActions: {
    marginTop: theme.spacing(1),
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  actionButton: {
    textTransform: "none",
  },
  confidenceBar: {
    marginTop: theme.spacing(1),
    height: 4,
    borderRadius: 2,
  },
}));

const GPTSuggestionsModal = ({
  open,
  onClose,
  suggestions = [],
  confidence,
  quickActions = [],
  onSelectSuggestion,
  loading = false,
  onGenerate,
  initialPrompt = "",
  companyId,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [customPrompt, setCustomPrompt] = React.useState(initialPrompt);
  const [currentLanguage, setCurrentLanguage] = React.useState(getInitialLanguageFromI18n());

  // Load GPT language setting
  React.useEffect(() => {
    const loadLanguage = async () => {
      if (companyId) {
        const lang = await getGPTLanguage(companyId);
        setCurrentLanguage(lang);
      }
    };
    loadLanguage();
  }, [companyId]);

  const t = (key, params = {}) => getGPTTranslation(key, currentLanguage, params);

  const handleSelect = (suggestion) => {
    if (onSelectSuggestion) {
      onSelectSuggestion(suggestion);
    }
    onClose();
  };

  const getConfidenceColor = (conf) => {
    if (conf >= 0.8) return "primary";
    if (conf >= 0.6) return "default";
    return "secondary";
  };

  // Detect if text is RTL (Hebrew or Arabic)
  const detectTextDirection = (text) => {
    if (!text || typeof text !== 'string') return 'ltr';
    
    // Check for Hebrew characters (U+0590-U+05FF)
    const hebrewPattern = /[\u0590-\u05FF]/;
    // Check for Arabic characters (U+0600-U+06FF)
    const arabicPattern = /[\u0600-\u06FF]/;
    
    // Count RTL characters
    let rtlCount = 0;
    let totalChars = 0;
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const code = char.charCodeAt(0);
      
      if ((code >= 0x0590 && code <= 0x05FF) || // Hebrew
          (code >= 0x0600 && code <= 0x06FF)) {  // Arabic
        rtlCount++;
      }
      if (/\S/.test(char)) { // Non-whitespace
        totalChars++;
      }
    }
    
    // If more than 30% of characters are RTL, use RTL direction
    if (totalChars > 0 && (rtlCount / totalChars) > 0.3) {
      return 'rtl';
    }
    
    return 'ltr';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      classes={{ paper: classes.dialog }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant={isMobile ? "subtitle1" : "h6"}>{t("gptSuggestions")}</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {/* Custom Prompt Field */}
        <Box marginBottom={3}>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            label={t("customPromptOptional")}
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder={t("customPromptPlaceholderModal")}
            helperText={t("customPromptHelperModal")}
            disabled={loading}
          />
          {onGenerate && (
            <Box marginTop={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => onGenerate(customPrompt)}
                disabled={loading}
                fullWidth
              >
                {loading ? t("generating") : t("generateSuggestions")}
              </Button>
            </Box>
          )}
        </Box>

        {loading ? (
          <Box>
            <LinearProgress />
            <Typography variant="body2" style={{ marginTop: 16, textAlign: "center" }}>
              {t("generatingSuggestions")}
            </Typography>
          </Box>
        ) : suggestions.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            {onGenerate ? t("enterPromptAndClick") : t("noSuggestionsAvailable")}
          </Typography>
        ) : (
          <>
            {suggestions.map((suggestion, index) => {
              const textDirection = detectTextDirection(suggestion);
              return (
                <Box
                  key={index}
                  className={classes.suggestionItem}
                  onClick={() => handleSelect(suggestion)}
                  style={{ direction: textDirection, textAlign: textDirection === 'rtl' ? 'right' : 'left' }}
                >
                  <Typography 
                    variant="body1" 
                    className={classes.suggestionText}
                    style={{ direction: textDirection, textAlign: 'initial' }}
                  >
                    {suggestion}
                  </Typography>
                {confidence !== undefined && (
                  <Box>
                    <Chip
                      label={t("confidence", { percent: Math.round(confidence * 100) })}
                      size="small"
                      color={getConfidenceColor(confidence)}
                      className={classes.confidenceChip}
                    />
                    <LinearProgress
                      variant="determinate"
                      value={confidence * 100}
                      className={classes.confidenceBar}
                      color={getConfidenceColor(confidence)}
                    />
                  </Box>
                )}
                </Box>
              );
            })}
            {quickActions.length > 0 && (
              <Box className={classes.quickActions}>
                <Typography variant="caption" color="textSecondary">
                  {t("quickActions")}
                </Typography>
                {quickActions.map((action, index) => (
                  <Button
                    key={index}
                    size="small"
                    variant="outlined"
                    className={classes.actionButton}
                    onClick={() => {
                      // Handle quick action
                      console.log("Quick action:", action);
                    }}
                  >
                    {action}
                  </Button>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GPTSuggestionsModal;

